import type { FunctionComponent } from 'react';
import { useRef } from 'react';
import type { ButtonProps } from './Button';
import Button from './Button';
import type { TooltipProps } from '../Tooltip';
import Tooltip from '../Tooltip';

export type TooltipButtonProps = Omit<TooltipProps, 'children'> & ButtonProps;

/**
 * A Button wrapped in a Tooltip component.
 *
 * @param {TooltipButtonProps} props - TooltipButton props
 * @param {ButtonType} [props.type] - Which type (style) of button to use.
 * @param {ButtonSize} [props.size] - Which size should the button be.
 * @param {IconClassName} [props.icon] - If a given icon should be rendered to the left of props.children.
 * @param {boolean} [props.isDisabled] - If the button should be disabled or not.
 * @param {React.ReactChild} [props.children] - The content of the button.
 * @param {(event: React.MouseEvent) => void} props.onClick - Click handler for the button.
 * @param {string | React.ReactNode} props.tooltip - Tooltip text. Can also be a ReactNode.
 * @param {'top' | 'bottom' | 'right' | 'left'} [props.placement] - Placement of the tooltip.
 * @param {boolean} [props.showTooltip] - If the tooltip should be displayed or not (children rendered as fallback).
 */
const TooltipButton: FunctionComponent<TooltipButtonProps> = ({
  children,
  tooltip,
  placement = 'top',
  showTooltip,
  ...buttonProps
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Tooltip
      id={buttonProps.id}
      tooltip={tooltip}
      placement={placement}
      showTooltip={showTooltip}
      onExited={() => {
        if (buttonRef.current) {
          // Reset the focus on the button to prevent unintended tooltip display upon returning to the tab.
          buttonRef.current.blur();
        }
      }}
    >
      <span className="inline-block">
        <Button
          ref={buttonRef}
          // We want to inject a large number of unknown props here, so spreading is necessary.
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...buttonProps}
        >
          {children}
        </Button>
      </span>
    </Tooltip>
  );
};

export default TooltipButton;
